import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import './MenuItemOptionsSheet.css';

export type MenuLinePromptState =
  | { kind: 'repeat'; summary: string }
  | { kind: 'multiple' };

interface MenuLinePromptProps {
  prompt: MenuLinePromptState | null;
  itemName: string;
  cartPath: string;
  onClose: () => void;
  onRepeat: () => void;
  onChooseNew: () => void;
}

/**
 * Swiggy/Zomato pattern for items with options already in the cart: "+" asks whether to repeat
 * the last customisation or choose again; "−" with several customisations sends the guest to the
 * cart to pick which one to remove.
 */
const MenuLinePrompt: React.FC<MenuLinePromptProps> = ({
  prompt,
  itemName,
  cartPath,
  onClose,
  onRepeat,
  onChooseNew,
}) => {
  useEffect(() => {
    if (!prompt) return undefined;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [prompt, onClose]);

  if (!prompt) return null;

  const repeat = prompt.kind === 'repeat';
  return createPortal(
    <div className="menu-options-sheet-overlay" onClick={onClose} role="presentation">
      <div
        className="menu-options-sheet-panel menu-line-prompt-panel"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="menu-line-prompt-title"
        aria-describedby="menu-line-prompt-desc"
      >
        <div className="menu-options-sheet-handle" aria-hidden="true" />
        <header className="menu-options-sheet-header">
          <div className="menu-options-sheet-header-text">
            <h3 id="menu-line-prompt-title">
              {repeat ? 'Repeat last customisation?' : 'Remove from cart'}
            </h3>
            <p className="menu-options-sheet-subtitle">{itemName}</p>
          </div>
          <button type="button" className="menu-options-sheet-close" onClick={onClose} aria-label="Close">
            <i className="fa fa-times" aria-hidden="true" />
          </button>
        </header>
        <div className="menu-line-prompt-body">
          <p id="menu-line-prompt-desc" className="menu-line-prompt-summary">
            {repeat
              ? prompt.summary || 'Same options as your last one.'
              : 'This item has more than one customisation in your cart. Remove the one you want from the cart.'}
          </p>
        </div>
        <footer className="menu-options-sheet-footer menu-line-prompt-footer">
          {repeat ? (
            <>
              <button
                type="button"
                className="menu-options-sheet-btn menu-options-sheet-btn--ghost"
                onClick={onChooseNew}
              >
                I&apos;ll choose
              </button>
              <button
                type="button"
                className="menu-options-sheet-btn menu-options-sheet-btn--primary"
                onClick={onRepeat}
                autoFocus
              >
                Repeat last
              </button>
            </>
          ) : (
            <>
              <button
                type="button"
                className="menu-options-sheet-btn menu-options-sheet-btn--ghost"
                onClick={onClose}
              >
                Cancel
              </button>
              <Link
                to={cartPath}
                className="menu-options-sheet-btn menu-options-sheet-btn--primary"
                onClick={onClose}
              >
                Go to cart
              </Link>
            </>
          )}
        </footer>
      </div>
    </div>,
    document.body
  );
};

export default MenuLinePrompt;
